import React from 'react';
import type { Role } from 'react-native';

import type { SvgIcon } from '@rover/types/src/SvgIcon';
import { Box, Flex, Text } from '@rover/kibble/core';
import { SpacingSystemType, StringSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import A11yHidden from '@rover/kibble/styles/A11yHidden';
import type { ThemeFontWeight, ThemeTextSizes } from '@rover/kibble/styles/themeTypes';
import useTheme from '@rover/kibble/themes/useTheme';
import { IconBlockVariants } from '@rover/types/src/IconBlockVariant';

import { getIconColors, TITLE_COLOR_FOR_VARIANT } from './IconBlock.common';

export type Props = {
  title: string;
  ariaLabel?: string;
  icon: SvgIcon;
  variant?: IconBlockVariants;
  children?: React.ReactNode;
  size?: ThemeTextSizes;
  iconSize?: string;
  fontWeight?: ThemeFontWeight;
  iconMargin?: SpacingSystemType;
  overrideTitleTopMargin?: SpacingSystemType;
  titleBoxFlex?: StringSystemType;
  iconColor?: string;
  a11yRole?: Role;
};

export const IconBlock = ({
  title,
  ariaLabel,
  icon: Icon,
  variant = 'default',
  size = '300',
  iconSize = '32px',
  fontWeight = 'semibold',
  iconMargin = '3x',
  overrideTitleTopMargin,
  children,
  titleBoxFlex = '1',
  iconColor,
  a11yRole,
}: Props): JSX.Element => {
  const theme = useTheme();
  const titleColor = TITLE_COLOR_FOR_VARIANT[variant];
  const topMargin = overrideTitleTopMargin || (size === '300' ? '1x' : '0x');

  return (
    <Flex flexDirection="row" alignItems="center">
      <Flex marginRight={iconMargin} aria-hidden>
        <Icon
          height={iconSize}
          width={iconSize}
          fill={iconColor ?? getIconColors(theme)[variant]}
        />
      </Flex>
      <Box flex={titleBoxFlex} mt={topMargin}>
        {/* only use the A11yHidden text on web, it breaks on native */}
        {!!ariaLabel && !process.env.JS_ENV_NATIVE && <Text sx={{ A11yHidden }}>{ariaLabel}</Text>}
        <Text
          fontWeight={fontWeight}
          textColor={titleColor}
          size={size}
          aria-hidden={!!ariaLabel}
          webProps={{ role: a11yRole }}
          {...(ariaLabel && process.env.JS_ENV_NATIVE ? { 'aria-label': ariaLabel } : {})}
        >
          {title}
        </Text>
        <Box aria-hidden={!!ariaLabel}>{children}</Box>
      </Box>
    </Flex>
  );
};

export default IconBlock;
